import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditDialog } from '../../../EditDialog';
import { EditDynamicSelectField } from '../../../EditDynamicSelectField';
import { EditFormField } from '../../../EditFormField';
import type { ContactOtherFieldsData } from '../queries/contactOthersQuery';
import { GET_CONTACT_OTHERS } from '../queries/contactOthersQuery';

const UPDATE_CONTACT = gql`
  mutation UpdateContact($id: ID!, $values: ContactInput!) {
    updateContact(id: $id, values: $values) {
      ok
      contact {
        id
        rowKey
        visitorKey
        importSequenceNumber
        process
        subscription
        autoCreated
        doNotShowInViewsJpOnly
        forceUpdateJpTest
        sqlDate
        soundex
        viewSuppress
        parentCustomerType
      }
    }
  }
`;

interface FieldMapping {
  apiField: string;
  formField: keyof ContactOtherFieldsData;
  isBoolean?: boolean;
  isDate?: boolean;
  isNumber?: boolean;
}

const IDENTIFIERS_SYSTEM_INFO_FIELDS: FieldMapping[] = [
  { apiField: 'rowKey', formField: 'rowKey' },
  { apiField: 'visitorKey', formField: 'visitorKey' },
  {
    apiField: 'importSequenceNumber',
    formField: 'importSequenceNumber',
    isNumber: true
  },
  { apiField: 'process', formField: 'process' },
  { apiField: 'subscription', formField: 'subscription' },
  { apiField: 'autoCreated', formField: 'autoCreated', isBoolean: true },
  {
    apiField: 'doNotShowInViewsJpOnly',
    formField: 'doNotShowInViewsJpOnly',
    isBoolean: true
  },
  { apiField: 'forceUpdateJpTest', formField: 'forceUpdateJpTest' },
  { apiField: 'sqlDate', formField: 'sqlDate', isDate: true },
  { apiField: 'soundex', formField: 'soundEx' },
  { apiField: 'viewSuppress', formField: 'viewSuppress', isBoolean: true },
  { apiField: 'parentCustomerType', formField: 'parentCustomerType' }
];

export interface EditIdentifiersSystemInfoDialogProps {
  contactId: string;
  initialValue: ContactOtherFieldsData;
  isOpen: boolean;
  onChange: (data: ContactOtherFieldsData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactOtherFieldsData;
}

export function EditIdentifiersSystemInfoDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditIdentifiersSystemInfoDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateContact] = useMutation(UPDATE_CONTACT, {
    refetchQueries: [{ query: GET_CONTACT_OTHERS, variables: { contactId } }]
  });

  const changes = getChangedValues(
    IDENTIFIERS_SYSTEM_INFO_FIELDS,
    value,
    initialValue
  );
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof ContactOtherFieldsData>(
    field: K,
    fieldValue: ContactOtherFieldsData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateContact({
        variables: { id: contactId, values: changes }
      });

      const data = response.data as {
        updateContact?: { ok?: boolean };
      };

      if (data.updateContact?.ok === false) {
        toast.error('Failed to save identifiers & system info');
      } else {
        toast.success('Identifiers & system info saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving identifiers & system info:', error);
      toast.error('Failed to save identifiers & system info');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Identifiers & System Info"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditFormField
          label="Row Key"
          onChange={v => {
            updateField('rowKey', v);
          }}
          value={value.rowKey}
        />
        <EditFormField
          label="Visitor Key"
          onChange={v => {
            updateField('visitorKey', v);
          }}
          value={value.visitorKey}
        />
        <EditFormField
          label="Import Sequence Number"
          onChange={v => {
            updateField('importSequenceNumber', v ? parseInt(v) : null);
          }}
          type="number"
          value={value.importSequenceNumber?.toString() ?? ''}
        />
        <EditFormField
          label="Process"
          onChange={v => {
            updateField('process', v);
          }}
          value={value.process}
        />
        <EditFormField
          label="Subscription"
          onChange={v => {
            updateField('subscription', v);
          }}
          value={value.subscription}
        />
        <EditCheckboxField
          checked={value.autoCreated}
          label="Auto-created"
          onCheckedChange={v => {
            updateField('autoCreated', v);
          }}
        />
        <EditCheckboxField
          checked={value.doNotShowInViewsJpOnly}
          label="Do Not Show in Views (JP Only)"
          onCheckedChange={v => {
            updateField('doNotShowInViewsJpOnly', v);
          }}
        />
        <EditDynamicSelectField
          fieldName="force_update_jp_test"
          label="ForceUpdateJPTest"
          model="res.partner"
          onChange={v => {
            updateField('forceUpdateJpTest', v);
          }}
          value={value.forceUpdateJpTest}
        />
        <EditFormField
          label="SQL Date"
          onChange={v => {
            updateField('sqlDate', v);
          }}
          type="date"
          value={toDateInputValue(value.sqlDate)}
        />
        <EditFormField
          label="SoundEx"
          onChange={v => {
            updateField('soundEx', v);
          }}
          value={value.soundEx}
        />
        <EditCheckboxField
          checked={value.viewSuppress}
          label="View Suppress"
          onCheckedChange={v => {
            updateField('viewSuppress', v);
          }}
        />
        <EditFormField
          label="Parent Customer Type"
          onChange={v => {
            updateField('parentCustomerType', v);
          }}
          value={value.parentCustomerType}
        />
      </div>
    </EditDialog>
  );
}

function getChangedValues(
  fields: FieldMapping[],
  current: ContactOtherFieldsData,
  initial: ContactOtherFieldsData
): Record<string, boolean | null | number | string> {
  const changes: Record<string, boolean | null | number | string> = {};

  for (const { apiField, formField, isBoolean, isDate, isNumber } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    let hasChanged = false;
    if (isDate) {
      hasChanged =
        toDateInputValue(currentVal as string) !==
        toDateInputValue(initialVal as string);
    } else {
      hasChanged = currentVal !== initialVal;
    }

    if (hasChanged) {
      if (isBoolean) {
        changes[apiField] = currentVal as boolean;
      } else if (isNumber) {
        changes[apiField] = currentVal as null | number;
      } else if (isDate) {
        const dateVal = currentVal as string;
        changes[apiField] = dateVal === '' ? null : dateVal;
      } else {
        const strVal = currentVal as string;
        changes[apiField] = strVal === '' ? null : strVal;
      }
    }
  }

  return changes;
}

function toDateInputValue(isoDate: string): string {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  return isNaN(date.getTime()) ? isoDate : date.toISOString().split('T')[0];
}
